import { createElement, ReactElement, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import { IHenshuApp } from '../HenshuApp';
import { IEachList } from '../store/IHenshuDataStore';


type Selection<T> = {
    i: number;
    item: T;
    ref: Element;
};

type EachProps<T> = {
    app: IHenshuApp,
    children(child: T, i: number): ReactElement;
    in: IEachList<T>,
    max?: number;
};


export default inject('app')(observer(function Each<T>(props: EachProps<T>) {
    const [selection, setSelection] = useState<Selection<T>|null>(null);
    const { isEditing } = props.app;
    const { items } = props.in;
    let selectionPosition = {};

    useEffect(() => {
        if (!isEditing && selection) {
            setSelection(null);
        }
    }, [ isEditing, selection ]);

    if (selection) {
        const { top, left, height, width } = selection.ref.getBoundingClientRect();

        selectionPosition = {
            top: `${top}px`,
            left: `${left}px`,
            height: `${height}px`,
            width: `${width}px`,
        };
    }

    return <>
        {items.map((item: T, i: number) => {
            let child = props.children(item, i);
            let ref: Element;

            return isEditing ? (
                createElement(
                    child.type,
                    {
                        ...child.props,
                        ref: (r: Element) => ref = r,
                        onMouseEnter(e: MouseEvent) {
                            if (child.props.onMouseEnter) {
                                child.props.onMouseEnter(e);
                            }

                            setSelection({ i, item, ref });
                        },
                        onMouseOver(e: MouseEvent) {
                            if (child.props.onMouseOver) {
                                child.props.onMouseOver(e);
                            }

                            setSelection({ i, item, ref });
                        },
                        onMouseLeave(e: MouseEvent) {
                            if (child.props.onMouseLeave) {
                                child.props.onMouseLeave(e);
                            }

                            setTimeout(() => selection && selection.ref === ref && setSelection(null), 50);
                        },
                    }
                )
            ) : (
                child
            );
        })}

        {selection && (
            <div className="Henshu__Each-selection" style={selectionPosition}>
                <div className="Henshu__Each-toolbar">
                    <button 
                        className={items.length === 1 ? 'disabled' : ''}
                        disabled={items.length === 1}
                        onClick={() => props.in.remove(selection.item)}
                    >
                        x
                    </button>

                    <button 
                        className={selection.i === 0 ? 'disabled' : ''}
                        disabled={selection.i === 0}
                        onClick={() => props.in.move(selection.item, selection.i - 1)}
                    >
                        &larr;
                    </button>

                    <button 
                        className={selection.i === items.length ? 'disabled' : ''}
                        disabled={selection.i === items.length - 1}
                        onClick={() => props.in.move(selection.item, selection.i + 1)}
                    >
                        &rarr;
                    </button>

                    <button 
                        className={props.max !== undefined && items.length === props.max ? 'disabled' : ''}
                        disabled={props.max !== undefined && items.length === props.max}
                        onClick={() => props.in.create(selection.i + 1)}
                    >
                        +
                    </button>
                </div>
            </div>
        )}
    </>;
}));
